import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message,
} from 'antd';
import {
  PlusOutlined,
  SettingOutlined,
  ThunderboltOutlined,
  EyeOutlined,
  EditOutlined,
  WarningFilled,
  ImportOutlined,
  DownloadOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, http } from '../api/client';
import { useBatches } from '../hooks/useBatches';

/**
 * Slice #8：业务「端口」绩效切档配置页。
 *
 * 三段：
 *   1. 端口配置表（CRUD）+ 「添加端口」按钮；_default 兜底配置永远在；H+M+L 必须 = 100
 *   2. 预览：选批次 → 后端按当前配置 + 当前批次绩效预估每个端口的 H/M/L 分布 + 未分配员工
 *   3. 执行：「按端口实际切档」按钮 → 更新 PerformanceScore.level（写审计日志）
 *
 * 与 Employee.port 配合：员工 port 字段在花名册编辑页填（或后续做导入），
 * 这里只管「配置 + 切档」。未分配 port 的员工走 _default 兜底。
 */

const DEFAULT_PORT = '_default';

interface CutoffRow {
  id: number | null;
  port: string;
  highPct: number;
  midPct: number;
  lowPct: number;
  remark: string | null;
  employeeCount: number;
  updatedAt: string | null;
}

interface PreviewPortRow {
  port: string;
  n: number;
  H: number;
  M: number;
  L: number;
  configured: boolean;
}

interface Preview {
  ports: PreviewPortRow[];
  totalEmployees: number;
  unassignedCount: number;
  unassignedSample: Array<{ empId: number; empNo: string; name: string }>;
  hasDefault: boolean;
}

interface PortImportCandidate {
  empId: number;
  empNo: string;
  name: string;
  position: string | null;
  deptPath: string | null;
  status: string | null;
}

interface PortImportPendingItem {
  rowNo: number;
  name: string;
  candidates: PortImportCandidate[];
  row: { name: string; empNo: string; port: string };
}

export function PortCutoffPage() {
  const qc = useQueryClient();
  const { batches, batchId, setBatchId } = useBatches();
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<CutoffRow | null>(null);
  const [form] = Form.useForm();
  const [renameOpen, setRenameOpen] = useState(false);
  const [renaming, setRenaming] = useState<CutoffRow | null>(null);
  const [renameForm] = Form.useForm();
  const [pendingOpen, setPendingOpen] = useState(false);
  const [pendingItems, setPendingItems] = useState<PortImportPendingItem[]>([]);
  const [pendingSelection, setPendingSelection] = useState<Record<string, number | undefined>>({});

  const { data: cutoffs = [], isFetching } = useQuery<CutoffRow[]>({
    queryKey: ['port-cutoffs'],
    queryFn: () => api.get('/admin/port-cutoffs') as Promise<CutoffRow[]>,
  });

  const { data: preview, isFetching: previewLoading, refetch: refetchPreview } = useQuery<Preview>({
    queryKey: ['port-cutoffs-preview', batchId],
    queryFn: () =>
      api.get('/admin/port-cutoffs/preview', { params: { batchId } }) as Promise<Preview>,
    enabled: !!batchId,
  });

  const saveMutation = useMutation({
    mutationFn: (vals: any) => api.put('/admin/port-cutoffs', vals),
    onSuccess: () => {
      message.success(editing && editing.id ? '已更新' : '已新增端口配置');
      setEditOpen(false);
      setEditing(null);
      form.resetFields();
      qc.invalidateQueries({ queryKey: ['port-cutoffs'] });
      qc.invalidateQueries({ queryKey: ['port-cutoffs-preview'] });
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message || e?.message || '保存失败');
    },
  });

  const removeMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/admin/port-cutoffs/${id}`),
    onSuccess: () => {
      message.success('已删除');
      qc.invalidateQueries({ queryKey: ['port-cutoffs'] });
      qc.invalidateQueries({ queryKey: ['port-cutoffs-preview'] });
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message || e?.message || '删除失败');
    },
  });

  const recomputeMutation = useMutation({
    mutationFn: () => api.post('/admin/port-cutoffs/recompute', { batchId }),
    onSuccess: (r: any) => {
      Modal.success({
        title: '按端口切档完成',
        content: `更新 ${r.updated} 行 / 未变更 ${r.unchanged} / 共 ${r.total} 行。已写审计日志，请到「九宫格」核算后查看新落格。`,
      });
      qc.invalidateQueries({ queryKey: ['port-cutoffs-preview'] });
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message || e?.message || '切档失败');
    },
  });

  const importMutation = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      return api.post('/admin/port-cutoffs/import-people', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    onSuccess: (r: any) => {
      const pending: PortImportPendingItem[] = Array.isArray(r?.pending) ? r.pending : [];
      Modal.success({
        title: '端口人员导入完成',
        content: (
          <div>
            <div>
              覆盖端口 <strong>{r.updated}</strong> 人 · 端口留空跳过 {r.skipped} · 新建端口配置{' '}
              {r.newPorts?.length ?? 0} 个
            </div>
            {r.newPorts?.length ? (
              <div style={{ marginTop: 6 }}>
                新端口（默认 30/50/20）：{r.newPorts.join('、')}
              </div>
            ) : null}
            {r.errors?.length ? (
              <div style={{ marginTop: 8, color: '#fa541c' }}>
                {r.errors.length} 行未导入：
                <ul style={{ margin: '4px 0 0 18px' }}>
                  {r.errors.slice(0, 10).map((e: any, i: number) => (
                    <li key={i}>
                      第 {e.rowNo} 行：{e.message}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {pending.length ? (
              <div style={{ marginTop: 8, color: '#1677ff' }}>
                同名待确认 {pending.length} 行（请在下一步选择具体员工）
              </div>
            ) : null}
          </div>
        ),
      });
      if (pending.length > 0) {
        const initSel: Record<string, number | undefined> = {};
        pending.forEach((item) => {
          if (item.candidates.length === 1) initSel[`row-${item.rowNo}`] = item.candidates[0].empId;
        });
        setPendingItems(pending);
        setPendingSelection(initSel);
        setPendingOpen(true);
      }
      qc.invalidateQueries({ queryKey: ['port-cutoffs'] });
      qc.invalidateQueries({ queryKey: ['port-cutoffs-preview'] });
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message || e?.message || '导入失败');
    },
  });

  const resolveImportMutation = useMutation({
    mutationFn: (items: Array<{ rowNo: number; empId?: number; row: { name: string; empNo: string; port: string } }>) =>
      api.post('/admin/port-cutoffs/import-people/resolve', { items }),
    onSuccess: (r: any, vars) => {
      const sentRowNos = new Set(vars.map((x) => x.rowNo));
      const nextPendingFromApi: PortImportPendingItem[] = Array.isArray(r?.pending) ? r.pending : [];
      const pendingRowNos = new Set(nextPendingFromApi.map((p) => p.rowNo));
      const errorRowNos = new Set((r?.errors || []).map((e: any) => Number(e.rowNo)));
      setPendingItems((prev) => {
        const keepOld = prev.filter(
          (p) => !sentRowNos.has(p.rowNo) || pendingRowNos.has(p.rowNo) || errorRowNos.has(p.rowNo),
        );
        const merged = [...keepOld.filter((p) => !pendingRowNos.has(p.rowNo)), ...nextPendingFromApi];
        if (merged.length === 0) setPendingOpen(false);
        return merged;
      });

      message.success(
        `已确认 ${r?.updated ?? 0} 行` + (r?.errors?.length ? `，失败 ${r.errors.length} 行` : ''),
      );
      qc.invalidateQueries({ queryKey: ['port-cutoffs'] });
      qc.invalidateQueries({ queryKey: ['port-cutoffs-preview'] });
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message || e?.message || '待确认处理失败');
    },
  });

  const renameMutation = useMutation({
    mutationFn: (vars: { oldPort: string; newPort: string }) =>
      api.put('/admin/port-cutoffs/rename', vars),
    onSuccess: (r: any) => {
      message.success(`已重命名为「${r.to}」：同步 ${r.employeesUpdated} 名员工`);
      setRenameOpen(false);
      setRenaming(null);
      renameForm.resetFields();
      qc.invalidateQueries({ queryKey: ['port-cutoffs'] });
      qc.invalidateQueries({ queryKey: ['port-cutoffs-preview'] });
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message || e?.message || '重命名失败');
    },
  });

  const downloadImportTemplate = async () => {
    try {
      const r = await http.get('/admin/port-cutoffs/import-template.xlsx', {
        responseType: 'blob',
      });
      const blob = new Blob([r.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = '端口人员导入模板.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      message.error(`模板下载失败：${(e as Error).message}`);
    }
  };

  const openRename = (r: CutoffRow) => {
    setRenaming(r);
    renameForm.resetFields();
    renameForm.setFieldsValue({ newPort: '' });
    setRenameOpen(true);
  };

  const openCreate = (presetPort?: string) => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      port: presetPort || '',
      highPct: 30,
      midPct: 50,
      lowPct: 20,
      remark: '',
    });
    setEditOpen(true);
  };

  const openEdit = (r: CutoffRow) => {
    setEditing(r);
    form.setFieldsValue({
      port: r.port,
      highPct: r.highPct,
      midPct: r.midPct,
      lowPct: r.lowPct,
      remark: r.remark || '',
    });
    setEditOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const vals = await form.validateFields();
      const sum = (vals.highPct ?? 0) + (vals.midPct ?? 0) + (vals.lowPct ?? 0);
      if (Math.abs(sum - 100) > 0.01) {
        message.warning(`高 + 中 + 低 % 必须等于 100，当前 ${sum}`);
        return;
      }
      saveMutation.mutate(vals);
    } catch {
      /* form 自校验已提示 */
    }
  };

  const resolveOnePending = (item: PortImportPendingItem) => {
    const key = `row-${item.rowNo}`;
    const empId = pendingSelection[key];
    if (!empId) {
      message.warning(`请先为第 ${item.rowNo} 行选择员工`);
      return;
    }
    resolveImportMutation.mutate([{ rowNo: item.rowNo, empId, row: item.row }]);
  };

  const resolveSelectedPending = () => {
    const items = pendingItems
      .map((item) => ({
        rowNo: item.rowNo,
        empId: pendingSelection[`row-${item.rowNo}`],
        row: item.row,
      }))
      .filter((x) => !!x.empId) as Array<{
      rowNo: number;
      empId: number;
      row: { name: string; empNo: string; port: string };
    }>;
    if (!items.length) {
      message.warning('请先选择至少 1 条待确认记录');
      return;
    }
    resolveImportMutation.mutate(items);
  };

  return (
    <div>
      <div className="tg-page-title">业绩端口 · 切档配置</div>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="按业务「端口」分组对绩效切档（H/M/L）"
        description={
          <ul style={{ margin: '4px 0 0 18px', padding: 0, fontSize: 12 }}>
            <li>员工业务端口在「花名册编辑」里从已配置端口中下拉选择，或用右上角「<strong>导入端口人员</strong>」按<strong>姓名</strong>批量赋；同名歧义会进入待确认清单，出现的新端口会自动建 30/50/20 配置。</li>
            <li>下表为每个端口配比例：高 + 中 + 低 % 之和必须等于 100。<strong>_default</strong> 是兜底，员工未填端口或端口未在表内时走它。</li>
            <li>「预览」会按当前配置 + 当前批次的 PerformanceScore.score 模拟切档；「按端口切档」才会真正更新 level。</li>
          </ul>
        }
      />

      <Card
        title={
          <Space>
            <SettingOutlined />
            <span>端口绩效切档配置</span>
            <Tooltip title="_default 是兜底配置，员工未填端口时走它，且不允许删除">
              <Tag color="purple">含 _default 兜底</Tag>
            </Tooltip>
          </Space>
        }
        extra={
          <Space wrap>
            <Button icon={<DownloadOutlined />} onClick={downloadImportTemplate}>
              下载导入模板
            </Button>
            <Upload
              accept=".xls,.xlsx"
              showUploadList={false}
              beforeUpload={(file) => {
                importMutation.mutate(file as File);
                return false;
              }}
            >
              <Button icon={<ImportOutlined />} loading={importMutation.isPending}>
                导入端口人员
              </Button>
            </Upload>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openCreate()}>
              添加端口
            </Button>
          </Space>
        }
        style={{ marginBottom: 16 }}
      >
        <Table<CutoffRow>
          rowKey={(r) => `${r.port}-${r.id ?? 'new'}`}
          loading={isFetching}
          dataSource={cutoffs}
          pagination={false}
          size="middle"
          columns={[
            {
              title: '端口',
              dataIndex: 'port',
              width: 160,
              render: (v: string) =>
                v === DEFAULT_PORT ? (
                  <Tag color="purple">_default（兜底）</Tag>
                ) : (
                  <strong>{v}</strong>
                ),
            },
            {
              title: '高 H %',
              dataIndex: 'highPct',
              align: 'right' as const,
              width: 90,
              render: (v: number, r) =>
                r.id == null ? (
                  <Typography.Text type="secondary">未配</Typography.Text>
                ) : (
                  <Tag color="red">{Number(v).toFixed(0)}%</Tag>
                ),
            },
            {
              title: '中 M %',
              dataIndex: 'midPct',
              align: 'right' as const,
              width: 90,
              render: (v: number, r) =>
                r.id == null ? (
                  <Typography.Text type="secondary">未配</Typography.Text>
                ) : (
                  <Tag color="gold">{Number(v).toFixed(0)}%</Tag>
                ),
            },
            {
              title: '低 L %',
              dataIndex: 'lowPct',
              align: 'right' as const,
              width: 90,
              render: (v: number, r) =>
                r.id == null ? (
                  <Typography.Text type="secondary">未配</Typography.Text>
                ) : (
                  <Tag color="blue">{Number(v).toFixed(0)}%</Tag>
                ),
            },
            { title: '备注', dataIndex: 'remark', ellipsis: true, render: (v) => v || '—' },
            {
              title: '在职员工数',
              dataIndex: 'employeeCount',
              align: 'right' as const,
              width: 110,
              render: (v: number, r) =>
                r.id == null ? (
                  <Tag color="orange" icon={<WarningFilled />}>{v} 人 · 未配置</Tag>
                ) : (
                  <span>{v} 人</span>
                ),
            },
            {
              title: '操作',
              key: 'op',
              width: 250,
              render: (_v, r) =>
                r.id == null ? (
                  <Space>
                    <Button type="primary" size="small" onClick={() => openCreate(r.port)}>
                      去配置
                    </Button>
                    {r.port !== DEFAULT_PORT && (
                      <Button size="small" icon={<SwapOutlined />} onClick={() => openRename(r)}>
                        重命名
                      </Button>
                    )}
                  </Space>
                ) : (
                  <Space>
                    <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>
                      编辑
                    </Button>
                    {r.port !== DEFAULT_PORT && (
                      <Button size="small" icon={<SwapOutlined />} onClick={() => openRename(r)}>
                        重命名
                      </Button>
                    )}
                    {r.port !== DEFAULT_PORT && (
                      <Popconfirm
                        title={`删除端口「${r.port}」？`}
                        description="该端口的员工会自动落入 _default 兜底切档。"
                        onConfirm={() => removeMutation.mutate(r.id!)}
                      >
                        <Button size="small" danger>
                          删除
                        </Button>
                      </Popconfirm>
                    )}
                  </Space>
                ),
            },
          ]}
        />
      </Card>

      <Card
        title={
          <Space>
            <EyeOutlined />
            <span>预览 · 按端口切档分布</span>
          </Space>
        }
        extra={
          <Space>
            <Select
              placeholder="选择批次"
              value={batchId}
              onChange={setBatchId}
              style={{ width: 220 }}
              options={batches.map((b) => ({ label: b.name, value: b.id }))}
            />
            <Button onClick={() => refetchPreview()} disabled={!batchId} loading={previewLoading}>
              刷新预览
            </Button>
          </Space>
        }
        style={{ marginBottom: 16 }}
      >
        {!batchId ? (
          <Typography.Text type="secondary">请先选择批次</Typography.Text>
        ) : preview ? (
          <div>
            <Space size={24} style={{ marginBottom: 16 }}>
              <Statistic title="本批次绩效行数" value={preview.totalEmployees} suffix="人" />
              <Statistic
                title="未分配端口"
                value={preview.unassignedCount}
                suffix="人"
                valueStyle={{ color: preview.unassignedCount > 0 ? '#fa541c' : '#52c41a' }}
              />
              <Tag color={preview.hasDefault ? 'green' : 'orange'}>
                {preview.hasDefault ? '_default 已配置' : '_default 未配置（用 30/50/20 兜底）'}
              </Tag>
            </Space>
            <Table<PreviewPortRow>
              rowKey="port"
              dataSource={preview.ports}
              pagination={false}
              size="small"
              columns={[
                {
                  title: '端口',
                  dataIndex: 'port',
                  width: 160,
                  render: (v) =>
                    v === DEFAULT_PORT ? <Tag color="purple">_default（兜底）</Tag> : <strong>{v}</strong>,
                },
                {
                  title: '人数',
                  dataIndex: 'n',
                  align: 'right' as const,
                  width: 80,
                },
                {
                  title: '预计 H',
                  dataIndex: 'H',
                  align: 'right' as const,
                  render: (v, r) => <Tag color="red">{v} 人 / {((v / Math.max(1, r.n)) * 100).toFixed(0)}%</Tag>,
                },
                {
                  title: '预计 M',
                  dataIndex: 'M',
                  align: 'right' as const,
                  render: (v, r) => <Tag color="gold">{v} 人 / {((v / Math.max(1, r.n)) * 100).toFixed(0)}%</Tag>,
                },
                {
                  title: '预计 L',
                  dataIndex: 'L',
                  align: 'right' as const,
                  render: (v, r) => <Tag color="blue">{v} 人 / {((v / Math.max(1, r.n)) * 100).toFixed(0)}%</Tag>,
                },
                {
                  title: '状态',
                  dataIndex: 'configured',
                  render: (v) => (v ? <Tag color="green">已配置</Tag> : <Tag color="orange">未配置 → 兜底</Tag>),
                },
              ]}
            />
            {preview.unassignedCount > 0 && (
              <Alert
                type="warning"
                showIcon
                style={{ marginTop: 12 }}
                message={`有 ${preview.unassignedCount} 人未在花名册填写「业务端口」`}
                description={
                  <div>
                    <div style={{ marginBottom: 4 }}>
                      切档时会走 <strong>_default</strong> 兜底（{preview.hasDefault ? '已配置' : '当前未配，将用 30/50/20 兜底'}）。如不希望走兜底，请到「花名册」给以下员工补填端口后重新预览。
                    </div>
                    <div style={{ fontSize: 12, color: '#666' }}>
                      未分配示例（最多展示 20）：{preview.unassignedSample.map((u) => `${u.name}(${u.empNo})`).join('、')}
                    </div>
                  </div>
                }
              />
            )}
          </div>
        ) : (
          <Typography.Text type="secondary">点击「刷新预览」</Typography.Text>
        )}
      </Card>

      <Card
        title={
          <Space>
            <ThunderboltOutlined />
            <span>执行 · 按端口切档</span>
          </Space>
        }
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Typography.Text type="secondary">
            点击下方按钮，会立刻按上方预览的分布更新当前批次所有员工的 PerformanceScore.level；操作不可撤销，但会写审计日志，可在「操作审计」回溯。
            执行后请到「九宫格」页面手工触发批次核算，新的 level 才会反映到九宫格里。
          </Typography.Text>
          <Popconfirm
            title="确认按端口实际切档？"
            description="会立即更新当前批次所有员工的业绩等级；建议先在上方「预览」确认分布无误。"
            onConfirm={() => recomputeMutation.mutate()}
            okText="确认执行"
            cancelText="取消"
            disabled={!batchId || !preview}
          >
            <Button
              type="primary"
              danger
              icon={<ThunderboltOutlined />}
              loading={recomputeMutation.isPending}
              disabled={!batchId || !preview}
              size="large"
            >
              按端口切档（写入 level）
            </Button>
          </Popconfirm>
        </Space>
      </Card>

      <Modal
        title={`同名待确认（${pendingItems.length}）`}
        open={pendingOpen}
        onCancel={() => setPendingOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setPendingOpen(false)}>
            关闭
          </Button>,
          <Button
            key="batch"
            type="primary"
            loading={resolveImportMutation.isPending}
            onClick={resolveSelectedPending}
          >
            批量确认已选择
          </Button>,
        ]}
        width={760}
        destroyOnClose
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="刷新或离开页面将丢失待确认列表，可重新导入同一文件继续处理。"
        />
        <Space direction="vertical" style={{ width: '100%' }} size={10}>
          {pendingItems.map((item, idx) => {
            const key = `row-${item.rowNo}`;
            return (
              <div
                key={key}
                style={{
                  paddingBottom: 10,
                  borderBottom: idx < pendingItems.length - 1 ? '1px dashed #e5e7eb' : undefined,
                }}
              >
                <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
                  <Typography.Text>
                    第 {item.rowNo} 行 · {item.name} · 端口 {item.row.port}
                  </Typography.Text>
                  <Space>
                    <Select
                      size="small"
                      style={{ minWidth: 360 }}
                      placeholder="选择员工"
                      value={pendingSelection[key]}
                      onChange={(v) => setPendingSelection((prev) => ({ ...prev, [key]: v }))}
                      options={item.candidates.map((c) => ({
                        value: c.empId,
                        label: `${c.empNo} · ${c.name} · ${c.position || '—'} · ${c.deptPath || '—'} · ${c.status || '—'}`,
                      }))}
                    />
                    <Button
                      size="small"
                      type="primary"
                      loading={resolveImportMutation.isPending}
                      onClick={() => resolveOnePending(item)}
                    >
                      确认
                    </Button>
                  </Space>
                </Space>
              </div>
            );
          })}
          {pendingItems.length === 0 && <Typography.Text type="secondary">暂无待确认记录</Typography.Text>}
        </Space>
      </Modal>

      <Modal
        title={editing && editing.id ? `编辑端口 · ${editing.port}` : '新增端口配置'}
        open={editOpen}
        onCancel={() => setEditOpen(false)}
        onOk={handleSubmit}
        confirmLoading={saveMutation.isPending}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="port"
            label="端口名"
            rules={[{ required: true, message: '端口名必填' }]}
            extra={
              <span>
                如「销售端」「医护端」「行政端」；填 <strong>_default</strong> 表示兜底配置。
              </span>
            }
          >
            <Input
              placeholder="如「销售端」"
              disabled={!!(editing && editing.id)}
              maxLength={50}
            />
          </Form.Item>
          <Space size={16} style={{ width: '100%' }}>
            <Form.Item name="highPct" label="高 H %" rules={[{ required: true }]}>
              <InputNumber min={0} max={100} addonAfter="%" />
            </Form.Item>
            <Form.Item name="midPct" label="中 M %" rules={[{ required: true }]}>
              <InputNumber min={0} max={100} addonAfter="%" />
            </Form.Item>
            <Form.Item name="lowPct" label="低 L %" rules={[{ required: true }]}>
              <InputNumber min={0} max={100} addonAfter="%" />
            </Form.Item>
          </Space>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            提示：高 + 中 + 低 % 必须等于 100。
          </Typography.Text>
          <Form.Item name="remark" label="备注" style={{ marginTop: 12 }}>
            <Input.TextArea rows={2} maxLength={200} placeholder="可选，记录配比理由" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`重命名端口 · ${renaming?.port ?? ''}`}
        open={renameOpen}
        onCancel={() => {
          setRenameOpen(false);
          setRenaming(null);
        }}
        onOk={async () => {
          try {
            const vals = await renameForm.validateFields();
            const newPort = (vals.newPort || '').trim();
            if (!renaming) return;
            if (newPort === renaming.port) {
              message.warning('新端口名与原名相同');
              return;
            }
            renameMutation.mutate({ oldPort: renaming.port, newPort });
          } catch {
            /* form 自校验已提示 */
          }
        }}
        confirmLoading={renameMutation.isPending}
        destroyOnClose
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="重命名会同步更新该端口的切档配置和所有该端口员工的「业务端口」，并写审计日志。"
        />
        <Form form={renameForm} layout="vertical">
          <Form.Item label="原端口名">
            <Input value={renaming?.port} disabled />
          </Form.Item>
          <Form.Item
            name="newPort"
            label="新端口名"
            rules={[{ required: true, message: '新端口名必填' }]}
          >
            <Input placeholder="如「销售部」" maxLength={50} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
